import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { getChatDocument } from '../../../../api/agents-client/agent-client';
import { useOrganizationDocuments } from '../../../../api/agents-client/agent-client.hooks';
import {
  useGoogleFiles,
  useGoogleStatus,
} from '../../../../api/google-integrations-client/google-integrations-client.hooks';
import type { GoogleDriveFile } from '../../../../api/google-integrations-client/google-integrations-client.type';
import { isSafeUrl } from '../../../../helpers/general-helpers';
import { Text } from '../../../../ui-library/typography/typography';
import { getFloraConnectionPath } from '../agent-settings/agent-settings.constants';
import { FloraConnectionId } from '../agent-settings/agent-settings.type';
import { AgentViewHeader } from '../agent-view-header';
import {
  buildTypeFilterOptions,
  filterDocuments,
  groupDocumentsByRecency,
  toUnifiedDocuments,
  type UnifiedDocument,
} from './agent-document.helpers';
import { DocPreviewModal } from './doc-preview-modal';
import { type DocumentActionHandlers, isFloraPreviewable } from './document-actions';
import { DocumentCard } from './document-card';
import { DocumentFilterMenu } from './document-filter-menu';
import { DocumentTable } from './document-table';
import { DocumentViewToggle, type DocumentView } from './document-view-toggle';
import { GoogleFilePreviewModal } from './google-file-preview-modal';
import { isPreviewable } from './google-file-preview.helpers';

const INTEGRATIONS_PAGE_PATH = getFloraConnectionPath(FloraConnectionId.Google);
/** Mirrors the backend `list_accessible_files` page size; used to flag truncation. */
const GOOGLE_FILES_PAGE_SIZE = 200;
/** Stable empty reference so the `unified` memo doesn't rebuild every render. */
const EMPTY_GOOGLE_FILES: GoogleDriveFile[] = [];

interface AgentDocumentsViewProps {
  onGoToChat: (chatHistoryId: string) => void;
}

export function AgentDocumentsView({ onGoToChat }: AgentDocumentsViewProps) {
  // Always refetch on open: Flora may have created or edited documents (here or
  // in another tab) since the list was cached.
  const { documents, isLoading, error } = useOrganizationDocuments({ refetchOnMount: 'always' });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [floraPreview, setFloraPreview] = useState<{
    documentId: string;
    filename: string;
    revision?: number;
    docType: string;
  } | null>(null);
  const [view, setView] = useState<DocumentView>('list');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const { data: googleStatus } = useGoogleStatus();
  const googleConnected = Boolean(googleStatus?.connected);
  const showGoogleSection = googleConnected;
  const {
    data: googleFilesData,
    isLoading: isLoadingGoogleFiles,
    isError: isGoogleFilesError,
  } = useGoogleFiles(showGoogleSection);
  const googleFiles = googleFilesData ?? EMPTY_GOOGLE_FILES;

  const unified = useMemo(
    () => toUnifiedDocuments(documents, showGoogleSection ? googleFiles : []),
    [documents, googleFiles, showGoogleSection],
  );
  const typeOptions = useMemo(() => buildTypeFilterOptions(unified), [unified]);
  const visible = useMemo(() => filterDocuments(unified, selectedTypes), [unified, selectedTypes]);
  // Grid/tile view only: bucket the sorted+filtered docs by recency, matching
  // the Flora chat-history sidebar's day/week/month labels. The table view stays
  // a flat list.
  const groupedVisible = useMemo(() => groupDocumentsByRecency(visible), [visible]);
  const hasFilters = selectedTypes.length > 0;
  const totalCount = unified.length;

  const previewFile = useMemo(
    () => unified.find((doc) => doc.id === previewFileId)?.google ?? null,
    [unified, previewFileId],
  );

  const handleDownload = async (doc: UnifiedDocument) => {
    if (!doc.flora) return;
    setDownloadingId(doc.id);
    try {
      // getChatDocument resolves to null on HTTP failure (it does not throw), so
      // a null result — or a missing/unsafe URL — must surface an error here
      // rather than silently doing nothing.
      const freshDoc = await getChatDocument(doc.flora.id);
      if (freshDoc?.download_url && isSafeUrl(freshDoc.download_url)) {
        window.open(freshDoc.download_url, '_blank', 'noopener,noreferrer');
      } else {
        notifications.show({ message: `Couldn't download "${doc.flora.filename}". Please try again.`, color: 'red' });
      }
    } catch {
      notifications.show({ message: `Couldn't download "${doc.flora.filename}". Please try again.`, color: 'red' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handlers: DocumentActionHandlers = {
    onPreview: (doc) => doc.google && setPreviewFileId(doc.id),
    onPreviewFlora: (doc, revision) => {
      if (!doc.flora) return;
      setFloraPreview({
        documentId: doc.flora.id,
        filename: doc.flora.filename,
        revision: revision ?? doc.revision,
        docType: doc.typeKey,
      });
    },
    onOpenGoogle: (doc) => {
      if (doc.google && isSafeUrl(doc.google.webViewLink)) {
        window.open(doc.google.webViewLink, '_blank', 'noopener,noreferrer');
      }
    },
    onGoToChat: (doc) => {
      if (doc.flora?.chat_history_id) onGoToChat(doc.flora.chat_history_id);
    },
    onDownload: (doc) => {
      if (downloadingId !== doc.id) void handleDownload(doc);
    },
    downloadingId,
  };

  // A row or card click previews whatever the app can show (Google files, and
  // Flora Word/Excel/text documents); anything else downloads. The download
  // icon always downloads.
  const onPrimary = (doc: UnifiedDocument) => {
    if (doc.source === 'google') {
      if (doc.google && isPreviewable(doc.google)) setPreviewFileId(doc.id);
    } else if (isFloraPreviewable(doc)) {
      handlers.onPreviewFlora(doc);
    } else {
      handlers.onDownload(doc);
    }
  };

  const clearFilters = () => setSelectedTypes([]);
  const toggleType = (typeKey: string) =>
    setSelectedTypes((current) =>
      current.includes(typeKey) ? current.filter((key) => key !== typeKey) : [...current, typeKey],
    );

  const subtitle = hasFilters
    ? `${visible.length} of ${totalCount} documents`
    : `${totalCount} ${totalCount === 1 ? 'document' : 'documents'}`;

  if (isLoading) {
    return (
      <Container>
        <AgentViewHeader title="Documents" />
        <LoadingWrapper>
          <Loader size="sm" color="var(--secondary-base)" />
        </LoadingWrapper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <AgentViewHeader title="Documents" />
        <EmptyState>
          <Icon icon="material-symbols:error-outline" width={48} height={48} color="var(--ink-lighter)" />
          <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
            Failed to load documents
          </Text>
        </EmptyState>
      </Container>
    );
  }

  // Nothing anywhere, and Google isn't still resolving — the true empty page.
  if (totalCount === 0 && !isLoadingGoogleFiles && !isGoogleFilesError) {
    return (
      <Container>
        <AgentViewHeader title="Documents" subtitle="0 documents" />
        <EmptyState>
          <Icon icon="material-symbols:description-outline" width={48} height={48} color="var(--ink-lighter)" />
          <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
            No documents yet
          </Text>
          <Text size="small" color="var(--ink-lighter)">
            Start chatting with Flora, or connect Google Drive, to see documents here.
          </Text>
        </EmptyState>
      </Container>
    );
  }

  const controls = (
    <>
      {typeOptions.length > 0 && (
        <DocumentFilterMenu
          options={typeOptions}
          selected={selectedTypes}
          onToggle={toggleType}
          onClear={clearFilters}
        />
      )}
      <DocumentViewToggle value={view} onChange={setView} />
    </>
  );

  return (
    <Container>
      <AgentViewHeader title="Documents" subtitle={subtitle} rightSlot={totalCount > 0 ? controls : undefined} />
      <ScrollArea>
        {isGoogleFilesError && (
          <SectionNote>
            <Text size="small" color="var(--ink-lighter)">
              Couldn't load your Google files. Your connection may need refreshing —{' '}
              <SectionLink to={INTEGRATIONS_PAGE_PATH}>manage integrations</SectionLink>.
            </Text>
          </SectionNote>
        )}

        {visible.length === 0 ? (
          // While Google files are still resolving, show a loader rather than a
          // premature "no matches" — more files may still arrive.
          isLoadingGoogleFiles ? (
            <SectionLoader>
              <Loader size="sm" color="var(--secondary-base)" />
            </SectionLoader>
          ) : hasFilters ? (
            <FilterEmpty>
              <Icon icon="material-symbols:filter-list-off" width={44} height={44} color="var(--ink-lighter)" />
              <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 8 }}>
                No files match this filter
              </Text>
              <ClearFiltersButton type="button" onClick={clearFilters}>
                Clear filters
              </ClearFiltersButton>
            </FilterEmpty>
          ) : null
        ) : view === 'grid' ? (
          <GroupList>
            {groupedVisible.map((group) => (
              <DocumentGroup key={group.label}>
                <GroupHeader>{group.label}</GroupHeader>
                <Grid>
                  {group.documents.map((doc) => (
                    <DocumentCard key={doc.id} doc={doc} handlers={handlers} onPrimary={onPrimary} />
                  ))}
                </Grid>
              </DocumentGroup>
            ))}
          </GroupList>
        ) : (
          <DocumentTable documents={visible} handlers={handlers} onPrimary={onPrimary} />
        )}

        {isLoadingGoogleFiles && visible.length > 0 && (
          <SectionLoader>
            <Loader size="xs" color="var(--secondary-base)" />
            <Text size="tiny" color="var(--ink-lighter)" style={{ marginLeft: 8 }}>
              Loading Google Drive files…
            </Text>
          </SectionLoader>
        )}

        {showGoogleSection && googleFiles.length >= GOOGLE_FILES_PAGE_SIZE && (
          <SectionNote>
            <Text size="tiny" color="var(--ink-lighter)">
              Showing the {GOOGLE_FILES_PAGE_SIZE} most recently modified Google files. Open older files directly in
              Google Drive.
            </Text>
          </SectionNote>
        )}
      </ScrollArea>

      <GoogleFilePreviewModal file={previewFile} onClose={() => setPreviewFileId(null)} />
      <DocPreviewModal
        documentId={floraPreview?.documentId ?? null}
        filename={floraPreview?.filename}
        revision={floraPreview?.revision}
        docType={floraPreview?.docType}
        onClose={() => setFloraPreview(null)}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  /* The app defaults to content-box (src/styles/index.css), so height:100% plus
     this padding would make the Container 80px taller than the viewport and the
     Shell's overflow:hidden would clip the bottom — leaving the last row
     unreachable. border-box folds the padding into the 100% height. */
  box-sizing: border-box;
  padding: 40px 0;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  gap: 4px;
  padding: 0 80px;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  /* Allow this flex child to shrink below its content height so it scrolls
     internally instead of overflowing the Container (whose overflow is clipped
     by the app Shell) — without this the last rows are unreachable. */
  min-height: 0;
  /* border-box (app default is content-box) so the bottom padding stays inside
     the flex-allocated height instead of adding to it. */
  box-sizing: border-box;
  /* Bottom breathing room so the final row/group clears the viewport edge. */
  padding: 0 80px 40px;
`;

const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DocumentGroup = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/* Recency bucket header — mirrors the Flora chat-history sidebar's date labels:
   tiny, bold, uppercase, muted. */
const GroupHeader = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-lighter);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
`;

const SectionLoader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 4px;
`;

const SectionNote = styled.div`
  padding: 8px 4px 12px;
`;

const SectionLink = styled(Link)`
  color: var(--secondary-base);
  font-weight: 500;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const FilterEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  gap: 6px;
  text-align: center;
`;

const ClearFiltersButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: var(--secondary-base);
`;
